import * as React from 'react';
import { withWidth } from '@material-ui/core';
import Helmet from 'react-helmet';
import type { WindowWidth } from '../../flowTypes';

const MIN_SCALE = 0.5;
const MAX_SCALE = 5;
const SETTLE_RANGE = 0.1;
const ADDITIONAL_LIMIT = 0.2;
const DOUBLE_TAP_THRESHOLD = 300;
const ANIMATION_SPEED = 0.1;
const RESET_ANIMATION_SPEED = 0.08;
const INITIAL_X = 0;
const INITIAL_Y = 0;
const INITIAL_SCALE = 1;

const settle = (val, target, range) => {
  const lowerRange = val > target - range && val < target;
  const upperRange = val < target + range && val > target;
  return lowerRange || upperRange ? target : val;
};

const inverse = x => x * -1;

const getPointFromTouch = (touch, element) => {
  const rect = element.getBoundingClientRect();
  return {
    x: touch.clientX - rect.left,
    y: touch.clientY - rect.top,
  };
};

const getMidpoint = (pointA, pointB) => ({
  x: (pointA.x + pointB.x) / 2,
  y: (pointA.y + pointB.y) / 2,
});

const getDistanceBetweenPoints = (pointA, pointB) => (
  Math.sqrt(((pointA.y - pointB.y) ** 2) + ((pointA.x - pointB.x) ** 2))
);

const between = (min, max, value) => Math.min(max, Math.max(min, value));

const withPinchZoom = (
  { disableBrowserZoom = true, enabledWidth = ['xs', 'sm', 'md', 'lg', 'xl'] }: { disableBrowserZoom?: boolean, enabledWidth?: Array<string> } = {},
) => (Component: React.Element) => {

  type State = {
    x: number,
    y: number,
    scale: number,
    width: number,
    height: number,
  };

  type Props = {
    pinchWidth: number,
    pinchHeight: number,
    width: WindowWidth,
  }

  class PinchZoomPan extends React.Component<Props, State> {

    state = {
      x: INITIAL_X,
      y: INITIAL_Y,
      scale: INITIAL_SCALE,
      width: this.props.pinchWidth,
      height: this.props.pinchHeight,
    };

    zoomTo = (scale, midpoint) => {
      const frame = () => {
        if (this.state.scale === scale) return;

        const distance = scale - this.state.scale;
        const targetScale = this.state.scale + (ANIMATION_SPEED * distance);

        this.zoom(settle(targetScale, scale, SETTLE_RANGE), midpoint);
        this.animation = requestAnimationFrame(frame);
      };

      this.animation = requestAnimationFrame(frame);
    };

    reset = () => {
      const frame = () => {
        if (this.state.scale === INITIAL_SCALE && this.state.x === INITIAL_X && this.state.y === INITIAL_Y) return;
        const distance = INITIAL_SCALE - this.state.scale;
        const distanceX = INITIAL_X - this.state.x;
        const distanceY = INITIAL_Y - this.state.y;

        const targetScale = settle(this.state.scale + (RESET_ANIMATION_SPEED * distance), INITIAL_SCALE, SETTLE_RANGE);
        const targetX = settle(this.state.x + (RESET_ANIMATION_SPEED * distanceX), INITIAL_X, SETTLE_RANGE);
        const targetY = settle(this.state.y + (RESET_ANIMATION_SPEED * distanceY), INITIAL_Y, SETTLE_RANGE);

        const nextWidth = this.props.pinchWidth * targetScale;
        const nextHeight = this.props.pinchHeight * targetScale;

        this.setState({
          x: targetX,
          y: targetY,
          scale: targetScale,
          width: nextWidth,
          height: nextHeight,
        }, () => {
          this.animation = requestAnimationFrame(frame);
        });
      };

      this.animation = requestAnimationFrame(frame);
    };

    handleTouchStart = (event) => {
      if (this.animation) {
        cancelAnimationFrame(this.animation);
      }
      if (event.touches.length === 2) this.handlePinchStart(event);
      if (event.touches.length === 1) this.handleTapStart(event);
    };

    handleTouchMove = (event) => {
      if (event.touches.length === 2) this.handlePinchMove(event);
      if (event.touches.length === 1) this.handlePanMove(event);
    };

    handleTouchEnd = (event) => {
      if (event.touches.length > 0) return;

      if (this.state.scale > MAX_SCALE) {
        this.zoomTo(MAX_SCALE, this.lastMidpoint);
        return;
      }

      if (this.state.scale < MIN_SCALE) {
        this.zoomTo(MIN_SCALE, this.lastMidpoint);
        return;
      }

      if (this.lastTouchEnd && this.lastTouchEnd + DOUBLE_TAP_THRESHOLD > event.timeStamp) {
        this.reset();
      }

      this.lastTouchEnd = event.timeStamp;
    };

    handleTapStart = (event) => {
      this.lastPanPoint = getPointFromTouch(event.touches[0], this.container);
    };

    handlePanMove = (event) => {
      if (this.state.scale === 1) return;

      event.preventDefault();

      const point = getPointFromTouch(event.touches[0], this.container);
      const nextX = this.state.x + point.x - this.lastPanPoint.x;
      const nextY = this.state.y + point.y - this.lastPanPoint.y;

      this.setState({
        x: between(this.props.pinchWidth - this.state.width, 0, nextX),
        y: between(this.props.pinchHeight - this.state.height, 0, nextY),
      });

      this.lastPanPoint = point;
    };

    handlePinchStart = (event) => {
      const pointA = getPointFromTouch(event.touches[0], this.container);
      const pointB = getPointFromTouch(event.touches[1], this.container);
      this.lastDistance = getDistanceBetweenPoints(pointA, pointB);
    };

    handlePinchMove = (event) => {
      event.preventDefault();
      const pointA = getPointFromTouch(event.touches[0], this.container);
      const pointB = getPointFromTouch(event.touches[1], this.container);
      const distance = getDistanceBetweenPoints(pointA, pointB);
      const midpoint = getMidpoint(pointA, pointB);
      const scale = between(MIN_SCALE - ADDITIONAL_LIMIT, MAX_SCALE + ADDITIONAL_LIMIT, this.state.scale * (distance / this.lastDistance));

      this.zoom(scale, midpoint);

      this.lastMidpoint = midpoint;
      this.lastDistance = distance;
    };

    zoom = (scale, midpoint) => {
      const nextWidth = this.props.pinchWidth * scale;
      const nextHeight = this.props.pinchHeight * scale;
      const nextX = this.state.x + (inverse(midpoint.x * scale) * (nextWidth - this.state.width) / nextWidth);
      const nextY = this.state.y + (inverse(midpoint.y * scale) * (nextHeight - this.state.height) / nextHeight);

      this.setState({
        width: nextWidth,
        height: nextHeight,
        x: nextX,
        y: nextY,
        scale,
      });
    };

    animation: AnimationFrameID;

    lastMidpoint: {x: number, y: number};

    lastPanPoint: {x: number, y: number};

    lastDistance: number;

    lastTouchEnd: number;

    container: React.Element;

    render() {

      const {
        pinchWidth, pinchHeight, width, ...restProps
      } = this.props;

      if (!enabledWidth.includes(width)) {
        return <Component {...restProps} />;
      }

      return (
        <React.Fragment>
          {
            disableBrowserZoom
            && (
            <Helmet>
              <meta
                name="viewport"
                content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
              />
            </Helmet>
            )
          }
          <div
            ref={ref => (this.container = ref)}
            onTouchStart={this.handleTouchStart}
            onTouchMove={this.handleTouchMove}
            onTouchEnd={this.handleTouchEnd}
            style={{
              width: pinchWidth || '100%',
              height: pinchHeight || '100%',
            }}
          >
            <Component
              {...restProps}
              pinchedX={this.state.x}
              pinchedY={this.state.y}
              pinchedScale={this.state.scale}
            />
          </div>
        </React.Fragment>
      );
    }

  }

  return withWidth()(PinchZoomPan);
};

export default withPinchZoom;
